import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { ResetStateDefaults } from '../base-action-class.model';
import { AssessmentWithSeverityActionDialogAction } from './assessment-with-severity-action-dialog.action';
import {
  AssessmentWithSeverityActionReference
} from '../../enums/assessment_with_severity_action/assessment-with-severity-action-source.enum';
import { finalize, map, Observable, throwError } from 'rxjs';
import {
  AssessmentWithSeverityActionRequestService
} from '../../services/request/assessment-with-severity-action-request.service';
import {
  AssessmentWithSeverityAction
} from '../../models/assessment-with-severity-action/assessment-with-severity-action.model';
import { RequestClientService } from '../../services/client/request-client.service';
import { delay } from 'rxjs/operators';
import {
  ChecklistTemplateOverallAssessmentAction
} from '../checklist-template-overall-assessment/checklist-template-overall-assessment.action';
import { ChecklistTemplateDetailXsState } from '../checklist-template/checklist-template-detail-xs.state';

interface AssessmentWithSeverityActionStateModel {
  active: boolean;
  reference: AssessmentWithSeverityActionReference;
  referenceId: string;
  assessmentsWithSeverityActions: AssessmentWithSeverityAction[];
  updatedValues: Map<string, Partial<AssessmentWithSeverityAction>>; //key: overall assessment id, key: updated value
  inProgress: boolean;
}

const getDefaults: () => AssessmentWithSeverityActionStateModel = () => ({
  active: false,
  reference: null,
  referenceId: null,
  assessmentsWithSeverityActions: null,
  updatedValues: null,
  inProgress: false
});

@State<AssessmentWithSeverityActionStateModel>({
  name: 'assessmentWithSeverityAction',
  defaults: getDefaults()
})
@Injectable()
export class AssessmentWithSeverityActionDialogXsState {

  //region INJECTIONS
  private assessmentWithSeverityActionRequestService = inject(AssessmentWithSeverityActionRequestService);
  private requestService = inject(RequestClientService);
  private store = inject(Store);
  //endregion

  //region SELECTORS
  @Selector()
  static active(state: AssessmentWithSeverityActionStateModel): boolean {
    return state.active;
  }

  @Selector()
  static assessmentsWithSeverityActions(state: AssessmentWithSeverityActionStateModel): AssessmentWithSeverityAction[] {
    return state.assessmentsWithSeverityActions;
  }

  @Selector()
  static inProgress(state: AssessmentWithSeverityActionStateModel): boolean {
    return state.inProgress;
  }

  //endregion

  //region ACTIONS
  @Action(AssessmentWithSeverityActionDialogAction.Init)
  public init(
    ctx: StateContext<AssessmentWithSeverityActionStateModel>,
    action: AssessmentWithSeverityActionDialogAction.Init
  ): Observable<void> {
    const state: AssessmentWithSeverityActionStateModel = ctx.getState();

    if (!action.reference) {
      return throwError(() => new Error('Reference cannot be null!'));
    }

    if (!action.referenceId) {
      return throwError(() => new Error('Reference id cannot be null!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    ctx.patchState({
      ...getDefaults(),
      active: true,
      reference: action.reference,
      referenceId: action.referenceId,
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    switch (action.reference) {
      case AssessmentWithSeverityActionReference.CHECKLIST: {
        return this.assessmentWithSeverityActionRequestService
          .getAssessmentsWithSeverityActionForChecklistTemplateVersion(action.referenceId)
          .pipe(
            delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
            map((assessmentsWithSeverityActions: AssessmentWithSeverityAction[]) => {
              const updatedValues: Map<string, Partial<AssessmentWithSeverityAction>> = new Map();
              for (const assessmentWitSeverityAction of assessmentsWithSeverityActions) {
                updatedValues.set(
                  assessmentWitSeverityAction.overallAssessmentId,
                  {
                    isNegative: assessmentWitSeverityAction.isNegative,
                    severityActions: assessmentWitSeverityAction.severityActions
                  }
                );
              }
              ctx.patchState({
                assessmentsWithSeverityActions,
                updatedValues
              });
            }),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }
      default: {
        throw new Error('Not implemented!');
      }
    }
  }

  @Action(AssessmentWithSeverityActionDialogAction.UpdateAssessment)
  public updateAssessment(
    ctx: StateContext<AssessmentWithSeverityActionStateModel>,
    action: AssessmentWithSeverityActionDialogAction.UpdateAssessment
  ): void {
    const state: AssessmentWithSeverityActionStateModel = ctx.getState();

    if (!action.overallAssessmentId) {
      throw new Error('Reference cannot be null!');
    }

    if (!action.updatedValue) {
      throw new Error('Updated value cannot be null!');
    }

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    const updatedValues: Map<string, Partial<AssessmentWithSeverityAction>> = new Map(
      state.updatedValues
    );

    updatedValues.set(
      action.overallAssessmentId,
      action.updatedValue
    );

    ctx.patchState({
      updatedValues
    });
  }

  @Action(AssessmentWithSeverityActionDialogAction.Save)
  public save(
    ctx: StateContext<AssessmentWithSeverityActionStateModel>
  ): Observable<void> {
    const state: AssessmentWithSeverityActionStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    const requestStartDate: Date = new Date();

    ctx.patchState({
      inProgress: true
    });

    const valuesToSave: AssessmentWithSeverityAction[] = [];

    for (const [key, value] of state.updatedValues) {
      valuesToSave.push({
        overallAssessmentId: key,
        ...value
      } as AssessmentWithSeverityAction);
    }

    switch (state.reference) {
      case AssessmentWithSeverityActionReference.CHECKLIST: {
        return this.assessmentWithSeverityActionRequestService
          .saveAssessmentsWithSeverityActionForChecklistTemplateVersion(
            state.referenceId,
            valuesToSave
          )
          .pipe(
            delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
            map(() => {
              ctx.setState(getDefaults());
              this.store.dispatch(
                new ChecklistTemplateOverallAssessmentAction.Fetch(
                  this.store.selectSnapshot(
                    ChecklistTemplateDetailXsState.checklistTemplate
                  )?.currentVersion?.id
                ));
            }),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }
      default: {
        throw new Error('Not implemented!');
      }
    }
  }

  @Action(AssessmentWithSeverityActionDialogAction.Delete)
  public delete(
    ctx: StateContext<AssessmentWithSeverityActionStateModel>
  ): Observable<void> {
    const state: AssessmentWithSeverityActionStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    const requestStartDate: Date = new Date();

    ctx.patchState({
      inProgress: true
    });

    switch (state.reference) {
      case AssessmentWithSeverityActionReference.CHECKLIST: {
        return this.assessmentWithSeverityActionRequestService
          .deleteAssessmentsWithSeverityActionOfChecklistTemplateVersion(state.referenceId)
          .pipe(
            delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
            map(() => {
              ctx.setState(getDefaults());
              this.store.dispatch(
                new ChecklistTemplateOverallAssessmentAction.Fetch(
                  this.store.selectSnapshot(
                    ChecklistTemplateDetailXsState.checklistTemplate
                  )?.currentVersion?.id
                ));
            }),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }
      default: {
        throw new Error('Not implemented!');
      }
    }
  }

  @Action([
    AssessmentWithSeverityActionDialogAction.Reset,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<AssessmentWithSeverityActionStateModel>): void {
    ctx.setState(getDefaults());
  }

  //endregion

}
